<template>
  <div>
    <modal
      :name="modalName" :width="'94%'" :height="'auto'" :max-width="1360"
      :scrollable="true" :adaptive="true" :click-to-close="false" :reset="true"
      class="mailgun-template-modal modal-not-overflow" @opened="focusClose" @before-close="onBeforeClose"
    >
      <article role="dialog" aria-modal="true" aria-labelledby="mailgun-template-title">
        <AppModalHeader ref="header" design="new" eyebrow="Mailgun · Templates" :title="title" title-id="mailgun-template-title" class="mailgun-template-modal__header" @close="close" />
        <div v-if="loading" class="mailgun-template-modal__state"><AppSpinner design="new" /></div>
        <div v-else-if="loadError" class="mailgun-template-modal__state" role="alert">
          <p>{{ loadError }}</p><AppButton design="new" theme="dark" @click.native="load">Retry</AppButton>
        </div>
        <template v-else-if="detail">
          <div class="mailgun-template-modal__body">
            <section aria-label="Current sending configuration">
              <div class="mailgun-template-modal__section-heading">
                <div class="mailgun-template-modal__title-with-help">
                  <h3>Used for live sending</h3>
                  <InfoTooltip content="These Mailgun references remain active while the local draft is prepared." aria-label="About live sending" />
                </div>
              </div>
              <div class="mailgun-template-modal__current-grid">
                <AppCard design="new" variant="panel" title="Mailgun template" title-tag="h3">
                  <template #actions><AppStatusBadge design="new" compact tone="blue" label="External template" /></template>
                  <dl class="mailgun-template-modal__summary-list">
                    <div><dt>ID</dt><dd><span :title="bindingId">{{ shortenedId }}</span><CopyButton v-if="bindingId" :value="bindingId" label="Mailgun ID" /></dd></div>
                    <div><dt>Name</dt><dd>{{ detail.binding && detail.binding.remote_name || '—' }}</dd></div>
                  </dl>
                </AppCard>
                <AppCard design="new" variant="panel" title="Current email" title-tag="h3">
                  <dl class="mailgun-template-modal__summary-list">
                    <div><dt>Version</dt><dd>{{ detail.binding && detail.binding.remote_version || '—' }}</dd></div>
                    <div><dt>Current subject</dt><dd>{{ detail.subject || '—' }}</dd></div>
                  </dl>
                </AppCard>
              </div>
            </section>

            <AppCard design="new" variant="panel" :title="editorMode === 'draft' ? 'Local draft' : 'Preview'" title-tag="h3" class="mailgun-template-modal__workspace">
              <template #title-help>
                <InfoTooltip :content="editorHelp" :aria-label="editorMode === 'draft' ? 'About the local draft' : 'About the preview'" />
              </template>
              <template #actions>
                <AppButton v-if="canEdit && editorMode === 'preview'" design="new" theme="dark" size="small" :disabled="previewing" @click.native="previewDraft">{{ previewing ? 'Rendering…' : 'Refresh preview' }}</AppButton>
                <AppButton v-if="canEdit" design="new" theme="dark" size="small" class="mailgun-template-modal__view-toggle" :aria-pressed="editorMode === 'preview' ? 'true' : 'false'" :disabled="previewing" @click.native="toggleEditorMode">
                  <svg v-if="editorMode === 'draft'" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M1.5 10S4.5 4.5 10 4.5s8.5 5.5 8.5 5.5-3 5.5-8.5 5.5S1.5 10 1.5 10Z" /><circle cx="10" cy="10" r="2.5" /></svg>
                  <svg v-else viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m7 5-5 5 5 5m6-10 5 5-5 5m-2-13-2 16" /></svg>
                  {{ editorMode === 'draft' ? (previewing ? 'Rendering…' : 'Preview') : 'Edit draft' }}
                </AppButton>
              </template>
              <div v-show="editorMode === 'draft'" class="mailgun-template-modal__editor">
                <AppFormField design="new" label="Future email subject" label-for="mailgun-draft-subject">
                  <AppInput id="mailgun-draft-subject" :value="form.draft_subject" :disabled="!canEdit" design="new" maxlength="512" placeholder="Subject shown to the recipient" @input="changeSubject" />
                </AppFormField>
                <AppFormField design="new" label="HTML content" label-for="mailgun-draft-html">
                  <AppTextarea id="mailgun-draft-html" :value="form.draft_html_body" :disabled="!canEdit" design="new" class="mailgun-template-modal__code" :placeholder="'<p>Hello {{ customer_name }},</p>\n{{{ signature_html }}}'" @input="changeHtml" />
                </AppFormField>
                <p v-if="previewError" class="mailgun-template-modal__error" role="alert">{{ previewError }}</p>
              </div>
              <div v-if="editorMode === 'preview'" class="mailgun-template-modal__preview">
                <div v-if="previewing" class="mailgun-template-modal__preview-loading"><AppSpinner design="new" /></div>
                <template v-else-if="preview">
                  <p class="mailgun-template-modal__preview-subject"><span>Subject</span> {{ preview.subject }}</p>
                  <iframe ref="previewFrame" :srcdoc="safePreview" :style="{ height: `${previewHeight}px` }" title="Local email draft preview" sandbox="allow-same-origin" referrerpolicy="no-referrer" scrolling="no" @load="resizePreview" />
                  <p v-if="preview.missing_values.length" class="mailgun-template-modal__hint">Example values are missing for: {{ preview.missing_values.join(', ') }}.</p>
                </template>
                <p v-else class="mailgun-template-modal__muted">Refresh the preview to see the updated values.</p>
              </div>
            </AppCard>

            <div class="mailgun-template-modal__tools-grid">
              <AppCard design="new" tag="aside" variant="panel" title="Variables" title-tag="h3" class="mailgun-template-modal__variables">
                <template #title-help><InfoTooltip :content="'Variables are detected automatically from the subject and HTML. Use {{ name }} for escaped text and {{{ name }}} in the HTML body only for trusted raw HTML. Add a description for each variable to save the draft. Preview values are optional for preview and required for a test email.'" aria-label="About variables" /></template>
                <p v-if="!names.length" class="mailgun-template-modal__muted">No variables in this draft.</p>
                <AppDataTable v-else design="new" :columns="variableColumns" :rows="variableRows" row-key="name" min-width="440px" class="mailgun-template-modal__variable-table" aria-label="Template variables">
                  <template #cell-name="{ row }">
                    <code :title="variableLabel(row.name)" class="mailgun-template-modal__variable-name">{{ row.name }}</code>
                  </template>
                  <template #header-description>Description <em>*</em></template>
                  <template #cell-description="{ row }">
                    <AppInput :id="`description-${row.name}`" :value="form.draft_variables[row.name]" :disabled="!canEdit" :aria-label="`Description for ${row.name}`" :aria-invalid="!form.draft_variables[row.name] ? 'true' : 'false'" aria-required="true" design="new" :error="!form.draft_variables[row.name]" maxlength="300" placeholder="What it means" @input="setDescription(row.name, $event)" @keydown.native.stop />
                  </template>
                  <template #cell-preview="{ row }">
                    <AppInput :id="`sample-${row.name}`" :value="values[row.name]" :disabled="!canEdit" :aria-label="`Preview value for ${row.name}`" design="new" maxlength="4000" placeholder="Example value" @input="setValue(row.name, $event)" @keydown.native.stop />
                  </template>
                </AppDataTable>
              </AppCard>

              <AppCard v-if="canEdit" design="new" variant="panel" title="Send a test email" title-tag="h3" class="mailgun-template-modal__test">
                <template #title-help><InfoTooltip content="Sends the saved local draft directly through Mailgun. It does not change business sending." aria-label="About test emails" /></template>
                <div class="mailgun-template-modal__test-controls">
                  <AppFormField design="new" label="Test recipient" label-for="mailgun-test-recipient">
                    <AppInput id="mailgun-test-recipient" ref="recipient" :value="recipient" design="new" type="email" placeholder="name@example.com" @input="setRecipient" />
                  </AppFormField>
                  <AppButton ref="testSend" design="new" theme="success" :disabled="!canTestSend" @click.native="requestTestSend">{{ sending ? 'Sending…' : 'Send test email' }}</AppButton>
                </div>
                <p v-if="isDirty" class="mailgun-template-modal__hint">Save the draft before sending a test.</p>
                <p v-if="testError" class="mailgun-template-modal__error" role="alert">{{ testError }}</p>
                <p v-if="testResult" :class="testResult.status === 'sent' ? 'mailgun-template-modal__success' : 'mailgun-template-modal__error'" role="status">
                  {{ testMessage }}
                </p>
              </AppCard>
            </div>
          </div>
          <footer class="mailgun-template-modal__footer">
            <p v-if="saveError" class="mailgun-template-modal__error" role="alert">{{ saveError }}</p>
            <AppButton design="new" theme="dark" @click.native="close">Close</AppButton>
            <AppButton v-if="canEdit" design="new" theme="success" :disabled="saving || !isDirty" @click.native="save">{{ saving ? 'Saving…' : 'Save local draft' }}</AppButton>
          </footer>
        </template>
      </article>
    </modal>
    <modal
      :name="confirmationModalName" :width="460" :height="'auto'" :adaptive="true" :scrollable="true" :reset="true"
      class="mailgun-template-confirmation modal-not-overflow" @opened="focusConfirmationClose" @closed="onConfirmationClosed"
    >
      <section role="dialog" aria-modal="true" aria-labelledby="mailgun-template-confirmation-title">
        <AppModalHeader ref="confirmationHeader" design="new" :title="confirmationTitle" title-id="mailgun-template-confirmation-title" class="mailgun-template-confirmation__header" @close="cancelConfirmation" />
        <div v-if="confirmationAction === 'discard'" class="mailgun-template-confirmation__body">
          <p>Your latest draft changes have not been saved.</p>
          <p>You can keep editing or close this template without saving them.</p>
        </div>
        <div v-else class="mailgun-template-confirmation__body">
          <p>Send the saved local draft to:</p>
          <strong class="mailgun-template-confirmation__recipient">{{ confirmationRecipient }}</strong>
          <p>This will send a real email through Mailgun.</p>
        </div>
        <footer class="mailgun-template-modal__footer">
          <AppButton design="new" theme="dark" @click.native="cancelConfirmation">{{ confirmationAction === 'discard' ? 'Keep editing' : 'Cancel' }}</AppButton>
          <AppButton v-if="confirmationAction === 'discard'" design="new" theme="error" :disabled="saving || sending" @click.native="discardChanges">Discard changes</AppButton>
          <AppButton v-else design="new" theme="success" :disabled="sending || !canTestSend" @click.native="sendTest">Confirm and send</AppButton>
        </footer>
      </section>
    </modal>
  </div>
</template>

<script>
import { getSupportMailgunTemplate, patchSupportMailgunTemplate, postSupportMailgunPreview, postSupportMailgunTestSend } from '@/http'
import { newRequestId, safeHtml } from '@/views/tech-support/integrations/model'
import { techSupportKey } from '@/utils/techSupportAccess'
import { syncDescriptions, variableNames } from './variables'
import CopyButton from '@/views/tech-support/integrations/CopyButton.vue'
import InfoTooltip from './InfoTooltip.vue'

export default {
  name: 'MailgunTemplateModal',
  components: { CopyButton, InfoTooltip },
  props: { templateId: { type: Number, required: true } },
  data() {
    return {
      modalName: `mailgun-template-${this.templateId}`,
      detail: null,
      form: { draft_subject: '', draft_html_body: '', draft_variables: {} },
      variableColumns: [
        { key: 'name', label: 'Variable', width: '28%' },
        { key: 'description', label: 'Description', width: '36%' },
        { key: 'preview', label: 'Preview value', width: '36%' }
      ],
      saved: '',
      values: {},
      preview: null,
      editorMode: 'draft',
      previewHeight: 420,
      recipient: '',
      testRequestKey: null,
      confirmationRecipient: '',
      confirmationAction: '',
      loading: true,
      saving: false,
      previewing: false,
      sending: false,
      loadError: '',
      saveError: '',
      previewError: '',
      testError: '',
      testResult: null
    }
  },
  computed: {
    canEdit() { return this.$store.getters['permissions/can'](techSupportKey('mailgun', 'edit')) },
    title() { return this.detail ? this.detail.name : 'Mailgun template' },
    confirmationModalName() { return `${this.modalName}-confirmation` },
    confirmationTitle() { return this.confirmationAction === 'discard' ? 'Discard unsaved changes?' : 'Send a test email?' },
    editorHelp() {
      if (this.editorMode === 'draft') return 'This is a local draft for a later switch. Live messages still use the external template. Use {{ variable_name }} for escaped text. Use {{{ variable_name }}} only in the HTML body to insert trusted HTML without escaping.'
      return 'Preview uses the current editor content and example values, including unsaved changes. External images and interactive content are blocked.'
    },
    canTestSend() { return this.canEdit && !this.sending && Boolean(this.recipient.trim()) && !this.isDirty && this.draftReady },
    names() { return variableNames(this.form.draft_subject, this.form.draft_html_body) },
    variableRows() { return this.names.map(name => ({ name })) },
    isDirty() { return Boolean(this.detail) && JSON.stringify(this.form) !== this.saved },
    draftReady() { return Boolean(this.form.draft_subject.trim() && this.form.draft_html_body.trim()) },
    safePreview() { return safeHtml(this.preview?.html_body || '') },
    bindingId() { return this.detail?.binding?.remote_id || '' },
    shortenedId() { return this.bindingId.length > 20 ? `${this.bindingId.slice(0, 8)}…${this.bindingId.slice(-8)}` : this.bindingId || '—' },
    testMessage() {
      if (!this.testResult) return ''
      if (this.testResult.status === 'sent') return `Mailgun accepted the test email for ${this.testResult.recipient}. Delivery is not yet confirmed.`
      if (this.testResult.status === 'pending') return 'The test send is still in progress. Do not send it again yet.'
      if (this.testResult.status === 'unknown') return 'Mailgun result is unknown. Check delivery before trying again.'
      return 'Mailgun rejected the test email. Check the configuration and try again.'
    }
  },
  mounted() { this.$modal.show(this.modalName); this.load(); window.addEventListener('resize', this.resetPreviewHeight) },
  beforeDestroy() { window.removeEventListener('resize', this.resetPreviewHeight) },
  methods: {
    async load() {
      this.loading = true
      this.loadError = ''
      try {
        const { data } = await getSupportMailgunTemplate(this.templateId)
        this.detail = data
        this.form = {
          draft_subject: data.draft_subject || '',
          draft_html_body: data.draft_html_body || '',
          draft_variables: { ...(data.draft_variables || {}) }
        }
        this.saved = JSON.stringify(this.form)
      } catch (error) { this.loadError = this.errorMessage(error, 'Unable to load this template.') } finally { this.loading = false }
    },
    changeSubject(value) { this.form.draft_subject = value || ''; this.syncVariables() },
    changeHtml(value) { this.form.draft_html_body = value || ''; this.syncVariables() },
    syncVariables() {
      this.form.draft_variables = syncDescriptions(this.form.draft_subject, this.form.draft_html_body, this.form.draft_variables)
      this.values = Object.keys(this.form.draft_variables).reduce((next, name) => { next[name] = this.values[name] || ''; return next }, {})
      this.preview = null
      this.previewError = ''
      this.testResult = null
      this.testRequestKey = null
    },
    setDescription(name, value) { this.$set(this.form.draft_variables, name, value || ''); this.previewError = '' },
    setValue(name, value) { this.$set(this.values, name, value || ''); this.preview = null; this.previewError = ''; this.testRequestKey = null },
    setRecipient(value) { this.recipient = value || ''; this.testRequestKey = null },
    async save() {
      if (!this.canEdit) return
      this.saving = true
      this.saveError = ''
      try {
        const { data } = await patchSupportMailgunTemplate(this.templateId, this.form)
        this.detail = data
        this.form = { draft_subject: data.draft_subject, draft_html_body: data.draft_html_body, draft_variables: { ...data.draft_variables } }
        this.saved = JSON.stringify(this.form)
        this.testRequestKey = null
        this.$emit('saved')
      } catch (error) { this.saveError = this.errorMessage(error, 'Unable to save the local draft.') } finally { this.saving = false }
    },
    toggleEditorMode() {
      if (this.editorMode === 'preview') { this.editorMode = 'draft'; return }
      this.previewDraft()
    },
    async previewDraft() {
      if (!this.canEdit || this.previewing) return
      this.previewError = ''
      if (!this.draftReady) {
        this.preview = null
        this.previewError = 'Subject and HTML are required for preview.'
        this.editorMode = 'draft'
        return
      }
      this.previewing = true
      this.previewHeight = 420
      const source = JSON.stringify({ ...this.form, values: this.values })
      try {
        const { data } = await postSupportMailgunPreview(this.templateId, { ...this.form, values: this.values })
        if (source === JSON.stringify({ ...this.form, values: this.values })) {
          this.preview = data
          this.editorMode = 'preview'
        }
      } catch (error) {
        if (source === JSON.stringify({ ...this.form, values: this.values })) {
          this.preview = null
          this.previewError = this.errorMessage(error, 'Unable to render this draft.')
          this.editorMode = 'draft'
        }
      } finally { this.previewing = false }
    },
    resetPreviewHeight() { this.previewHeight = 420; this.$nextTick(this.resizePreview) },
    resizePreview() {
      const document = this.$refs.previewFrame?.contentDocument
      if (!document?.body) return
      this.previewHeight = Math.max(420, document.documentElement.scrollHeight, document.body.scrollHeight)
    },
    requestTestSend() {
      if (!this.canTestSend) return
      if (!this.$refs.recipient.$el.checkValidity()) { this.$refs.recipient.$el.reportValidity(); return }
      this.confirmationRecipient = this.recipient.trim()
      this.confirmationAction = 'send'
      this.$modal.show(this.confirmationModalName)
    },
    cancelConfirmation() { this.$modal.hide(this.confirmationModalName) },
    onConfirmationClosed() {
      const action = this.confirmationAction
      this.confirmationRecipient = ''
      this.confirmationAction = ''
      if (action === 'discard') this.focusClose()
      else if (this.$refs.testSend) this.$refs.testSend.$el.focus()
    },
    focusConfirmationClose() { this.$nextTick(() => this.$refs.confirmationHeader?.focusClose()) },
    async sendTest() {
      if (this.confirmationAction !== 'send' || !this.canTestSend || !this.confirmationRecipient || this.confirmationRecipient !== this.recipient.trim()) return
      const recipient = this.confirmationRecipient
      this.sending = true
      this.$modal.hide(this.confirmationModalName)
      this.testError = ''
      this.testResult = null
      if (!this.testRequestKey) this.testRequestKey = newRequestId()
      try {
        const { data } = await postSupportMailgunTestSend(this.templateId, {
          recipient, values: { ...this.values }, confirmed: true, request_key: this.testRequestKey
        })
        this.testResult = data
        if (data.status === 'failed') this.testRequestKey = null
      } catch (error) {
        if (!error.response || error.response.status >= 500) {
          this.testResult = { status: 'unknown', recipient }
        } else {
          this.testError = this.errorMessage(error, 'Unable to send the test email.')
        }
      } finally { this.sending = false }
    },
    errorMessage(error, fallback) { return error.response?.data?.details || fallback },
    variableLabel(name) { return `{{ ${name} }}` },
    focusClose() { this.$nextTick(() => this.$refs.header?.focusClose()) },
    discardChanges() {
      if (this.confirmationAction !== 'discard' || this.saving || this.sending) return
      this.$modal.hide(this.confirmationModalName)
      this.$emit('close')
    },
    close() {
      if (this.confirmationAction) { this.cancelConfirmation(); return }
      if (this.isDirty) {
        this.confirmationAction = 'discard'
        this.$modal.show(this.confirmationModalName)
        return
      }
      this.$emit('close')
    },
    onBeforeClose(event) { event.cancel(); this.close() }
  }
}
</script>

<style lang="scss" scoped>
.mailgun-template-modal { color: $ui-text; font-family: $ui-font-body; }
.mailgun-template-modal :deep(.vm--modal), .mailgun-template-confirmation :deep(.vm--modal) { border-radius: $ui-overlay-radius !important; }
.mailgun-template-modal__header, .mailgun-template-confirmation__header { border-radius: $ui-overlay-radius $ui-overlay-radius 0 0; }
.mailgun-template-modal__body { display: grid; gap: $ui-space-20; padding: $ui-space-24; background: $ui-surface-soft; }
.mailgun-template-modal__state { padding: $ui-space-32; text-align: center; }
.mailgun-template-modal__section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: $ui-space-12; margin-bottom: $ui-space-16; }
.mailgun-template-modal__section-heading h3 { margin: 0 0 $ui-space-4; color: $ui-ink; font: 600 $ui-font-size-section-title $ui-font-heading; }
.mailgun-template-modal__section-heading p { margin: 0; color: $ui-muted; font-size: $ui-font-size-body; }
.mailgun-template-modal__title-with-help { display: flex; align-items: center; gap: $ui-space-8; }
.mailgun-template-modal__title-with-help h3 { margin: 0; }
.mailgun-template-modal__current-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $ui-space-16; }
.mailgun-template-modal__summary-list { display: grid; gap: $ui-space-12; margin: 0; }
.mailgun-template-modal__summary-list > div { display: grid; grid-template-columns: minmax(90px, .65fr) minmax(0, 1.35fr); align-items: center; gap: $ui-space-12; }
.mailgun-template-modal__summary-list dt { color: $ui-muted; font-size: $ui-font-size-body; }
.mailgun-template-modal__summary-list dd { display: flex; align-items: center; justify-content: flex-end; gap: $ui-space-8; min-width: 0; margin: 0; overflow-wrap: anywhere; font-size: $ui-font-size-body; font-weight: 600; text-align: right; }
.mailgun-template-modal__summary-list ::v-deep .integration-copy { min-width: $ui-control-height-compact; min-height: $ui-control-height-compact; border: 1px solid $ui-line; background: $ui-surface-soft; color: $ui-text; }
.mailgun-template-modal__tools-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); align-items: start; gap: $ui-space-20; }
.mailgun-template-modal__view-toggle svg { width: 20px; height: 20px; stroke: currentColor; stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round; }
.mailgun-template-modal__preview-loading { display: grid; place-items: center; min-height: 540px; }
.mailgun-template-modal__editor .form-field + .form-field { margin-top: $ui-space-20; }
.mailgun-template-modal__editor .mailgun-template-modal__code { min-height: 540px; font-family: ui-monospace, SFMono-Regular, Consolas, monospace; line-height: 1.5; resize: vertical; }
.mailgun-template-modal__variable-table { min-height: 0; }
.mailgun-template-modal__variable-table ::v-deep th { height: 40px; }
.mailgun-template-modal__variable-table ::v-deep td { padding: $ui-space-8; }
.mailgun-template-modal__variable-name { display: block; overflow: hidden; color: $ui-blue; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.mailgun-template-modal__variables em { font-style: normal; color: $ui-red; }
.mailgun-template-modal__hint, .mailgun-template-modal__muted { color: $ui-muted; font-size: $ui-font-size-metadata; }
.mailgun-template-modal__preview iframe { display: block; width: 100%; border: 1px solid $ui-line; border-radius: $ui-control-radius; background: white; }
.mailgun-template-modal__preview-subject { margin: 0 0 $ui-space-12; }
.mailgun-template-modal__preview-subject span { margin-right: $ui-space-12; color: $ui-muted; }
.mailgun-template-modal__test-controls { display: grid; justify-items: start; gap: $ui-space-16; }
.mailgun-template-modal__test-controls .form-field { width: 100%; }
.mailgun-template-confirmation { color: $ui-text; font-family: $ui-font-body; }
.mailgun-template-confirmation__body { padding: $ui-space-20 $ui-space-24; font-size: $ui-font-size-body; }
.mailgun-template-confirmation__body p { margin: 0 0 $ui-space-12; }
.mailgun-template-confirmation__body p:last-child { margin-top: $ui-space-16; margin-bottom: 0; color: $ui-muted; }
.mailgun-template-confirmation__recipient { display: block; color: $ui-ink; overflow-wrap: anywhere; }
.mailgun-template-modal__footer { position: sticky; bottom: 0; display: flex; align-items: center; justify-content: flex-end; gap: $ui-space-12; padding: $ui-space-16 $ui-space-24; border-top: 1px solid $ui-line; border-radius: 0 0 $ui-overlay-radius $ui-overlay-radius; background: $ui-surface; }
.mailgun-template-modal__footer .mailgun-template-modal__error { margin-right: auto; }
.mailgun-template-modal__error { color: $ui-red; font-size: $ui-font-size-body; }
.mailgun-template-modal__success { color: $ui-green; font-size: $ui-font-size-body; }
@media (max-width: 800px) { .mailgun-template-modal__tools-grid, .mailgun-template-modal__current-grid { grid-template-columns: 1fr; } }
@media (max-width: 560px) {
  .mailgun-template-modal__body { padding: $ui-space-16; }
  .mailgun-template-modal__section-heading { flex-wrap: wrap; }
  .mailgun-template-modal__editor .mailgun-template-modal__code { min-height: 420px; }
}
</style>
